import { wireCopyButton } from "./copy-button.js";
import { debounced } from "./debounce.js";
import { wireExamples } from "./examples.js";
import {
  findSegmenterElements,
  type SegmenterElements,
} from "./segmenter-elements.js";
import { wireOrdering } from "./segmenter-ordering.js";
import { SegmenterSession } from "./segmenter-session.js";
import { wireShareLink } from "./share-link.js";

/**
 * The segmenter: Chinese text in, the words it is made of out.
 *
 * The second tool here that needs a dictionary, and it opens on the same
 * `standard` tier the converter does. The bigger tier matters more here than
 * it does there, since a text full of names is a text full of words the
 * standard tier splits into their characters, and the offer is the same one.
 *
 * **The input is capped.** Nothing is sent anywhere and the segmentation is
 * linear, so the cap is about the page rather than about a server. See
 * `inputCap`, which lives with the view that enforces it.
 *
 * This file is the page half: what the template carries, what a click means
 * and what the tool is asked for in reply. The answers are in
 * `segmenter-session.ts`.
 */

/**
 * Start the tool on a container the template marked for it.
 *
 * Returns without doing anything where the container is not the shape this
 * expects or does not say where the dictionary is, which is what keeps a
 * markup change from throwing on every page the bundle loads on.
 */
export function startSegmenterTool(container: HTMLElement): void {
  const dictionaryUrl = container.dataset["dictionary"];
  const elements = findSegmenterElements(container);

  if (dictionaryUrl === undefined || elements === undefined) {
    return;
  }

  wireSegmenter(
    container,
    elements,
    new SegmenterSession(elements, dictionaryUrl),
  );
}

/**
 * Wire the page to the tool.
 *
 * The typing is debounced and the cap is not: holding the box to the cap has
 * to happen on the keystroke that ran past it, or a reader watches a few more
 * characters go in before the box takes them back.
 */
function wireSegmenter(
  container: HTMLElement,
  elements: SegmenterElements,
  session: SegmenterSession,
): void {
  const { input, copy } = elements;
  const scheduleSegment = debounced(() => {
    session.segment();
  });

  input.addEventListener("input", () => {
    session.holdToCap();
    scheduleSegment();
  });

  wireOrdering(elements.ordering, (order) => {
    session.reorder(order);
  });

  if (copy !== undefined) {
    wireCopyButton(copy, () => session.copied());
  }

  wireShareLink(container, () => session.state());

  wireExamples(container, (text) => {
    input.value = text;
    session.segment();
  });

  void session.open();
}
